import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  TextField,
  Grid,
  InputAdornment,
  Avatar,
  Checkbox,
  Tooltip,
  Button,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
} from "@mui/material";
import CalendarToday from "@mui/icons-material/CalendarToday";
import Timeline from "@mui/icons-material/Timeline";

import { hexToRgba } from "../../../Components/helpers/util";
import { Search, OpenInNew } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import ViewListIcon from "@mui/icons-material/ViewList";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  useCreateBugMutation,
  useGetListOfProjectsQuery,
  useLazyGetListOfBugsQuery,
  useLazyGetListOfProjectsQuery,
  useUpdateSprintStatusMutation,
  useUpdateBugIndexMutation,
  useUpdateSprintMutation,
  useGetListOfMembersQuery,
} from "@/features/bughurd/api";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import TaskCreateModal from "../bugherdComponents/TaskCreationModal";
import axios from "axios";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import {
  addRecentProject,
  getBackgroundColor,
  getOperatingSystemInfo,
  getRecentProjects,
} from "../utils";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import TaskFilterMenu from "../bugherdComponents/TaskFilterMenu";
import { FilterChips } from "../bugherdComponents/FilterChips";
import GroupBySelector from "./GroupBySelector";
import TaskGlobalSearch from "./TaskGlobalSearch";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

// Redux imports
import {
  selectSelectedItem,
  selectViewType,
  selectSearchQuery,
  selectFilters,
  selectGroupingConfig,
  selectApiData,
  selectActiveSprintIds,
  selectBacklogViewType,
  selectBoardState,
} from "@/features/bughurd/boardSelector";
import {
  setViewType,
  setSearchQuery,
  toggleTag,
  toggleAvatar,
  setSubGroupBy,
  setSortOrder,
  setStatusList,
  clearAllFilters,
  setSelectedAvatars,
  setSelectedItem,
  setBacklogViewType,
  setCreateTask,
} from "@/features/bughurd/slice";
import TaskCreateDrawer from "../bugherdComponents/TaskCreateDrawer";
import SprintCompleteModal from "./Sprint/SprintCompleteModal";
import { toast } from "react-toastify";
import { bgcolor } from "@mui/system";
import SprintStartModal from "./Sprint/SprintStartModal";

const VIEW_TYPE_STORAGE_KEY = "taskLayoutViewType";

interface TaskLayoutProps {
  children?: React.ReactNode;
  refreshColumnData?: any;
  refetchBugList?: any;
  refetchStatusList?: any;
  showSubGrouping?: boolean;
  handleGroupByChange?: any;
  refetchSprints?: any;
  view?: string;
  selectedSprint?: any;
  reactFlow?: any;
}

export const getInitials = (fullName: string) => {
  const nameParts = fullName?.split(" ");
  if (nameParts?.length === 1) {
    return nameParts?.[0].substring(0, 2).toUpperCase();
  }
  const initials = nameParts
    ?.map((part) => part.charAt(0).toUpperCase())
    .join("");
  return initials?.length > 2 ? initials?.substring(0, 2) : initials;
};

export default function TaskLayout({
  children,
  refreshColumnData,
  refetchBugList,
  refetchStatusList,
  showSubGrouping = false,
  handleGroupByChange,
  refetchSprints,
  selectedSprint,
  view,
  reactFlow,
}: TaskLayoutProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [detailView, setDetailView] = useState(false);
  const boardState = useAppSelector(selectBoardState);
  const menuItems = [
    {
      name: t("My Projects"),
      icon: <FolderOutlinedIcon />,
      disabled: false,
    },
    {
      name: t("Dashboard"),
      icon: <DashboardCustomizeOutlinedIcon />,
      disabled: true,
    },
  ];

  const selectedItem = useAppSelector(selectSelectedItem);
  const viewType = useAppSelector(selectViewType);
  const searchQuery = useAppSelector(selectSearchQuery);
  const filters = useAppSelector(selectFilters);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const apiData = useAppSelector(selectApiData);
  const activeSprintIds = useAppSelector(selectActiveSprintIds);
  const backlogViewType = useAppSelector(selectBacklogViewType);

  // Destructure Redux state
  const { selectedTags, selectedAvatars } = filters;

  const { groupBy, subGroupBy, sortOrder } = groupingConfig;
  const { tagList, projectDetails } = apiData;

  const boardType = projectDetails?.boardType;

  const projectListRef = useRef<HTMLDivElement>(null);
  const [currentView, setCurrentView] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(VIEW_TYPE_STORAGE_KEY) || "kanban";
    }
    return "kanban";
  });

  const {
    companyDetails,
    roleCodeDetails: myPermissions,
    currentUser,
  } = useAppSelector((state) => state.appGlobal);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const open = Boolean(anchorEl);
  const [isModalOpen, setIsModalOpen] = useState(boardState?.createTask);
  const [anchorElFilter, setAnchorElFilter] = useState<HTMLElement | null>(
    null,
  );
  const [isSprintCompleteModalOpen, setIsSprintCompleteModalOpen] =
    useState(false);
  const [sprintStartModalOpen, setSprintStartModalOpen] = useState(false);
  const [sprintId, setSprintId] = useState<string | null>(null);
  const openFilterMenu = Boolean(anchorElFilter);
  const baseUrl = window?.location?.origin;
  const isSniffer = baseUrl?.includes("sniffer") || false;

  // Project pagination states
  const [projectPage, setProjectPage] = useState(1);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [isLoadingMoreProjects, setIsLoadingMoreProjects] = useState(false);
  const [shouldRefetchInitial, setShouldRefetchInitial] = useState(false);

  // Refresh menu state
  const [anchorElRefresh, setAnchorElRefresh] = useState<HTMLElement | null>(
    null,
  );
  const [refreshMode, setRefreshMode] = useState("manual");
  const [refreshInterval, setRefreshIntervalState] =
    useState<NodeJS.Timeout | null>(null);
  const openRefreshMenu = Boolean(anchorElRefresh);

  const [anchorElMore, setAnchorElMore] = useState<HTMLElement | null>(null);
  const openMoreMenu = Boolean(anchorElMore);

  const [anchorElTags, setAnchorElTags] = useState<HTMLElement | null>(null);
  const openTagsMenu = Boolean(anchorElTags);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [updateSprint, { isLoading: isUpdatingSprint }] =
    useUpdateSprintMutation();
  const [getProjectsLazy] = useLazyGetListOfProjectsQuery();
  const [createBug, { isLoading: isCreatingBug }] = useCreateBugMutation();
  const [getLazyBugs] = useLazyGetListOfBugsQuery();
  const [updateSprintStatus, { isLoading: isUpdatingSprintStatus }] =
    useUpdateSprintStatusMutation();
  const [updateBugIndex] = useUpdateBugIndexMutation();

  const { data, isLoading, isFetching } = useGetListOfProjectsQuery(
    {
      searchName: projectSearch || "",
      page: 1,
      size: 5,
    },
    {
      skip: shouldRefetchInitial,
    },
  );
  const {
    data: companyMemberDataQuery,
    isLoading: isLoadingProjectMembers,
    isFetching: isFetchingProjectMembers,
  } = useGetListOfMembersQuery({
    projectId: id,
    page: 1,
    size: 100,
    searchName: "",
  });
  const isMyTasks: boolean = pathname.includes("/defect-tracker/my-tasks");
  const isFeedback: boolean = pathname.includes("/feedback");
  const isMyTaskView = selectedItem === "My Tasks" || isMyTasks;

  // Determine if the current user is a client based on project members data
  const isCurrentUserClient = useMemo(() => {
    // Return false while loading to avoid premature decisions
    if (isLoadingProjectMembers || isFetchingProjectMembers) {
      return false;
    }

    const projectMembers =
      companyMemberDataQuery?.data?.decryptedData?.items || [];
    const currentUserId = currentUser?._id;

    if (!currentUserId || projectMembers.length === 0) {
      return false;
    }

    // Find the current user in project members
    const currentUserMember = projectMembers.find(
      (member: any) =>
        member?.user?._id === currentUserId || member?._id === currentUserId,
    );

    // Check if user role is CLIENT
    return currentUserMember?.userRole === "CLIENT";
  }, [
    companyMemberDataQuery?.data?.decryptedData?.items,
    currentUser?._id,
    isLoadingProjectMembers,
    isFetchingProjectMembers,
  ]);

  useEffect(() => {
    if (data?.data?.decryptedData?.items) {
      setAllProjects(data.data.decryptedData.items);
      setProjectPage(1);
      const totalItems = data.data.decryptedData.totalCount || 0;
      setHasMoreProjects(data.data.decryptedData.items.length < totalItems);
    }
  }, [data]);

  useEffect(() => {
    if (!shouldRefetchInitial && data?.data?.decryptedData?.items) {
      setAllProjects(data.data.decryptedData.items);
      setProjectPage(1);
      const totalItems = data.data.decryptedData.totalCount || 0;
      setHasMoreProjects(data.data.decryptedData.items.length < totalItems);
    }
  }, [data, shouldRefetchInitial]);

  const loadMoreProjects = useCallback(async () => {
    if (isLoadingMoreProjects || !hasMoreProjects) return;

    setIsLoadingMoreProjects(true);
    try {
      const nextPage = projectPage + 1;
      const result = await getProjectsLazy({
        searchName: projectSearch || "",
        page: nextPage,
        size: 5,
      }).unwrap();

      if (result?.data?.decryptedData?.items) {
        const newProjects = result.data.decryptedData.items;
        setAllProjects((prev) => [...prev, ...newProjects]);
        setProjectPage(nextPage);

        const totalItems = result.data.decryptedData.totalCount || 0;
        const currentTotal = allProjects.length + newProjects.length;
        setHasMoreProjects(currentTotal < totalItems);
      }
    } catch (error) {
      console.error("Error loading more projects:", error);
    } finally {
      setIsLoadingMoreProjects(false);
    }
  }, [
    projectPage,
    projectSearch,
    hasMoreProjects,
    isLoadingMoreProjects,
    allProjects.length,
    getProjectsLazy,
  ]);

  // Scroll handler for project list
  const handleProjectListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

      if (isNearBottom && hasMoreProjects && !isLoadingMoreProjects) {
        loadMoreProjects();
      }
    },
    [hasMoreProjects, isLoadingMoreProjects, loadMoreProjects],
  );

  useEffect(() => {
    if (viewType && viewType !== currentView) {
      setCurrentView(viewType);
      if (typeof window !== "undefined") {
        localStorage.setItem(VIEW_TYPE_STORAGE_KEY, viewType);
      }
    }
  }, [viewType]);

  // Redux action handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(event.target.value));
  };

  const handleTagSelect = (tagName: string) => {
    dispatch(toggleTag(tagName));
  };

  const handleAvatarClick = (userId: string) => {
    if (userId === "unassigned") {
      // If selecting unassigned, clear all other avatars and only select unassigned
      dispatch(setSelectedAvatars(["unassigned"]));
    } else {
      // If selecting a regular user, remove unassigned and toggle the user
      const currentAvatars = selectedAvatars.filter(
        (id) => id !== "unassigned",
      );

      if (currentAvatars.includes(userId)) {
        // Remove the user if already selected
        const updatedAvatars = currentAvatars.filter((id) => id !== userId);
        dispatch(setSelectedAvatars(updatedAvatars));
      } else {
        // Add the user to selection
        dispatch(setSelectedAvatars([...currentAvatars, userId]));
      }
    }
  };

  // Updated getFilteredUsers function to include project owner
  // ✅ FIXED: Updated getFilteredUsers function to handle user objects properly
  const getFilteredUsers = () => {
    // Return empty array while loading
    if (isLoadingProjectMembers || isFetchingProjectMembers) {
      return [];
    }

    if (
      !projectDetails?.users ||
      !companyMemberDataQuery?.data?.decryptedData?.items
    ) {
      return [];
    } else {
      return companyMemberDataQuery?.data?.decryptedData?.items?.filter(
        (user) => user?.userRole !== "CLIENT",
      );
    }

    // // ✅ FIX: Extract _id from user objects in projectDetails.users
    // // projectDetails.users contains objects like: { _id: "...", userId: "...", email: "...", fullName: "..." }
    // const projectUserIds = (projectDetails?.users || []).map((user) => {
    //   // user is an object with _id property
    //   if (typeof user === "object" && user?._id) {
    //     return user._id;
    //   }
    //   // Fallback: if it's already a string ID
    //   return user;
    // });

    // const projectOwnerId = projectDetails?.owner; // Get project owner ID

    // // Include project owner in the user IDs if not already included
    // const allUserIds =
    // projectOwnerId && !projectUserIds.includes(projectOwnerId)
    // ? [...projectUserIds, projectOwnerId]
    // : projectUserIds;

    // return companyMemberDataQuery?.data?.decryptedData?.items?.filter(
    //   (member: any) => allUserIds.includes(member._id)
    // );
  };

  const handleSelectAll = () => {
    const filteredUsers = getFilteredUsers();
    const allUserIds = filteredUsers.map((user) => user?.user?._id || user._id);
    const allIdsWithUnassigned = [...allUserIds, "unassigned"];

    const allSelected = allIdsWithUnassigned.every((id) =>
      selectedAvatars.includes(id),
    );

    if (allSelected) {
      dispatch(setSelectedAvatars([]));
    } else {
      dispatch(setSelectedAvatars(allIdsWithUnassigned));
    }
  };

  const handleSubGroupByChange = (newSubGroupBy: string) => {
    dispatch(setSubGroupBy(newSubGroupBy));
    dispatch(clearAllFilters());
  };

  const handleSortOrderChange = (newSortOrder: string) => {
    dispatch(setSortOrder(newSortOrder));
  };

  const handleRefreshClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElRefresh(event.currentTarget);
  };

  const handleCloseRefreshMenu = () => {
    setAnchorElRefresh(null);
  };

  const handleRefreshModeChange = (mode: string) => {
    setRefreshMode(mode);

    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshIntervalState(null);
    }

    if (mode === "manual") {
      handleManualRefresh();
    } else {
      const intervalTime = mode === "30s" ? 30000 : 60000;

      handleManualRefresh();

      const interval = setInterval(() => {
        handleManualRefresh();
      }, intervalTime);

      setRefreshIntervalState(interval);
    }

    handleCloseRefreshMenu();
  };

  const handleManualRefresh = async () => {
    try {
      if (viewType !== "table") {
        await getLazyBugs({
          id: id || "",
          forBoard: true,
          search: searchQuery || "",
          assignTo: selectedAvatars?.length > 0 ? selectedAvatars : undefined,
          tags: selectedTags?.length > 0 ? selectedTags : undefined,
          timestamp: new Date().getTime(), // Force fresh request
        }).unwrap();

        if (refreshColumnData) {
          await refreshColumnData();
        }
      } else {
        if (refetchBugList) {
          await refetchBugList();
        }
        if (refetchStatusList) {
          await refetchStatusList();
        }
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFilter(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setAnchorElFilter(null);
  };

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
    setMemberSearch("");
    // Focus the search input after menu opens
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleSprintComplete = async (data: any) => {
    try {
      const payload: any = {
        status: "ARCHIVE",
        sprintId: data.sprintId,
      };
      if (data.moveOpenItemsTo == "backlog") {
        payload.moveItems = "BACKLOG";
      } else if (data.moveOpenItemsTo == "new_sprint") {
        payload.moveItems = "NEW_SPRINT";
      } else {
        payload.moveItems = null; //all task completed  0
        payload.sprint = data.moveOpenItemsTo;
      }
      await updateSprintStatus(payload).unwrap();
      toast.success(t("Sprint completed successfully"));
      setIsSprintCompleteModalOpen(false);

      dispatch(setSelectedItem("Backlog"));
      navigate(`/defect-tracker/projects/${id}/backlog`);
    } catch (error) {
      console.error("Error completing sprint:", error);
      toast.error(t("Failed to complete sprint"));
    }
  };

  const handleCloseSprintCompleteModal = () => {
    setIsSprintCompleteModalOpen(false);
  };

  const handleCloseMoreMenu = () => {
    setAnchorElMore(null);
  };

  const handleMemberSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberSearch(e.target.value);
    e.stopPropagation();
  };

  const handleTagsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTags(event.currentTarget);
  };

  const handleCloseTagsMenu = () => {
    setAnchorElTags(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setProjectSearch("");
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setDetailView(false);
    dispatch(setCreateTask(false));
  };

  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string,
  ) => {
    if (newValue !== null) {
      setCurrentView(newValue);
      if (typeof window !== "undefined") {
        localStorage.setItem(VIEW_TYPE_STORAGE_KEY, newValue);
      }
      dispatch(setViewType(newValue));
    }
  };

  const handleBacklogViewChange = (event, newView) => {
    if (newView !== null) {
      dispatch(setBacklogViewType(newView));
      dispatch(setViewType(newView));
    }
  };

  const handleTaskSubmit = async (
    taskData: any,
    attachments: any[],
    availableUsers: any[] = [],
  ) => {
    try {
      if (taskData.keepSettings) {
        if (availableUsers !== undefined) {
          const validUserIds = availableUsers.map((user) => user._id);
          const filteredAssignees = taskData.assignees.filter((assigneeId) =>
            validUserIds.includes(assigneeId),
          );

          const taskDataToSave = {
            ...taskData,
            assignees: filteredAssignees,
          };
          localStorage.setItem("taskDataKeep", JSON.stringify(taskDataToSave));
        } else {
          localStorage.setItem("taskDataKeep", JSON.stringify(taskData));
        }
      } else {
        localStorage.removeItem("taskDataKeep");
      }
      const epic = taskData?.epic;
      const screenshotsToUpload: any[] = [];
      if (taskData.screenShotKey) {
        screenshotsToUpload.push({
          fileType: "image/png",
          fileName: "element-screenshot.png",
          url: taskData.screenShotKey,
        });
      }

      if (taskData.fullPageScreenshotKey) {
        screenshotsToUpload.push({
          fileType: "image/png",
          fileName: "full-page-screenshot.png",
          url: taskData.fullPageScreenshotKey,
        });
      }

      let videoUploadResult = null;
      if (taskData.videoRecording) {
        const response = await fetch(taskData.videoRecording);
        const blob = await response.blob();
        const file = new File([blob], "recorded-video.mp4", {
          type: "video/mp4",
        });

        if (file?.size > 0) {
          const s3Res = await axios.post(
            `${
              import.meta.env.VITE_COMPANY_API_URL
            }/file/s3/generate-upload-url`,
            {
              fileName: file.name,
              uploadFor: "RECORDING",
              chunkUpload: true,
              fileSize: file.size,
            },
            {
              headers: {},
              withCredentials: true,
            },
          );

          if (!s3Res?.data?.parts || s3Res.data.parts.length === 0) {
            throw new Error("No upload parts received from server");
          }

          const parts = s3Res.data.parts;
          const uploadId = s3Res.data.uploadId;
          const key = s3Res.data.fileKey;

          const MAX_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB in bytes
          const totalSize = file.size;

          const fullChunksCount = Math.floor(totalSize / MAX_CHUNK_SIZE);
          const lastChunkSize = totalSize % MAX_CHUNK_SIZE;
          const totalChunksNeeded =
            lastChunkSize > 0 ? fullChunksCount + 1 : fullChunksCount;

          if (parts.length < totalChunksNeeded) {
            throw new Error(
              `Need ${totalChunksNeeded} parts but only received ${parts.length} from server`,
            );
          }

          const uploadedParts = [];

          for (let i = 0; i < totalChunksNeeded; i++) {
            const part = parts[i];
            const start = i * MAX_CHUNK_SIZE;
            const end =
              i < fullChunksCount
                ? start + MAX_CHUNK_SIZE
                : start + lastChunkSize;

            const chunk = file.slice(start, end);
            const blob = new Blob([chunk], { type: "video/mp4" });

            const uploadResponse = await axios.put(part.signedUrl, blob, {
              headers: {
                "Content-Type": "video/mp4",
              },
            });

            uploadedParts.push({
              PartNumber: part.partNumber,
              ETag: uploadResponse.headers.etag || uploadResponse.headers.ETag,
            });
          }

          const completeResponse = await axios.post(
            `${import.meta.env.VITE_COMPANY_API_URL}/file/s3/upload-complete`,
            {
              uploadId,
              fileKey: key,
              parts: uploadedParts,
            },
            {
              headers: {},
              withCredentials: true,
            },
          );

          videoUploadResult = completeResponse.data;
        }
      }

      const deviceDetails = await getOperatingSystemInfo();

      // Handle sprint-related payload
      // Sprint payload logic with validation
      let sprintPayload = {};

      if (taskData.boardType === "SCRUM") {
        // Check if currentSprint exists and is valid in the sprints array
        const isSprintValid =
          taskData.currentSprint &&
          boardState?.allSprints?.find(
            (sprint) => sprint._id === taskData.currentSprint,
          );

        if (isSprintValid) {
          // Sprint exists in the list, pass it
          sprintPayload = {
            currentSprint: taskData.currentSprint,
          };
        } else {
          // Sprint doesn't exist or is not selected, set to backlog
          sprintPayload = {
            isBackLog: true,
          };
        }
      }

      const payload = {
        summary: taskData.summary,
        description: taskData.description,
        severity: taskData.severity,
        bugStatus: taskData.status,
        tags: taskData.tags,
        assignTo: taskData.assignees,
        isSettings: taskData.keepSettings,
        pageUrl: taskData.pageUrl,
        project: taskData.project,
        jiraPriority: taskData.severity,
        jiraStatus: taskData.status,
        coordinates: [0, 0],
        selector: taskData.selector,
        mentionedUsers: [],
        platform: taskData.jiraProjectKey ? "JIRA" : "NONE",
        attachments: [...screenshotsToUpload, ...attachments].flat(),
        type: taskData?.type || "BUG",
        videoRecordings: videoUploadResult
          ? [
              {
                fileName: "recorded-video.mp4",
                fileType: "video/mp4",
                url: videoUploadResult.fileKey,
              },
            ]
          : [],
        // jiraConfig: {
        //   projectKey: taskData.jiraProjectKey,
        //   projectName: taskData.jiraProjectName,
        //   projectId: taskData.jiraProjectId,
        //   jiraDatas: taskData?.jiraDatas,
        // },
        dueDate: taskData.dueDate,
        startDate: taskData.startDate || null,
        estimation: taskData.estimation,
        deviceDetails: JSON.stringify(deviceDetails),
        linkBugs: taskData?.linkBugs,
        isRecurring: taskData?.isRecurring || false,
        recurringData: taskData?.recurringData || null,
        ...sprintPayload,
        ...(epic ? { epic } : {}),
        ...(taskData?.requiredSkillSet
          ? { requiredSkillSet: taskData.requiredSkillSet }
          : {}),
        // Include linked story if defect is linked to a story
        ...(taskData?.associateStoryId
          ? { story: taskData.associateStoryId }
          : {}),
        // Set bugFor based on user role: FEEDBACK for clients, BOARD for team members
        bugFor: isCurrentUserClient ? "FEEDBACK" : "BOARD",
        isFeedback: isCurrentUserClient,
      };

      // Create the bug and get the response
      const createdBugResponse = await createBug(payload).unwrap();

      // After successful creation, update bug index
      if (createdBugResponse && createdBugResponse?.data?.decryptedData) {
        const bugId = createdBugResponse?.data?.decryptedData?._id;
        if (bugId) {
          const bugUrl = `${window.location.origin}/defect-tracker/projects/${createdBugResponse?.data?.decryptedData?.project}/task-board?bugId=${bugId}`;
          try {
            await navigator.clipboard.writeText(bugUrl);
          } catch (err) {
            console.error("Failed to copy link:", err);
          }
          // window.open(bugUrl, "_blank");
        }
        try {
          await updateBugIndex({
            lexo: true,
            project: createdBugResponse?.data?.decryptedData?.project,
            nextId:
              boardState?.columns?.[
                createdBugResponse?.data?.decryptedData?.bugStatus
              ]?.tasks?.[0]?._id || null,
            prevId: null,
            movedId: createdBugResponse?.data?.decryptedData?._id,
          }).unwrap();
        } catch (error) {
          console.error("Error updating bug index:", error);
        }
      }

      setDetailView(false);
      dispatch(setCreateTask(false));
    } catch (error) {
      console.error("Error creating bug:", error);
    }
  };

  const handleProjectSelect = (project: any) => {
    handleClose();
    if (project?.boardType === "SCRUM") {
      dispatch(setViewType("backlog"));
      dispatch(setBacklogViewType("backlog"));
      // dispatch(setSelectedItem("Backlog"));
    } else {
      // dispatch(setSelectedItem("Task Board"));
    }
    dispatch(setSelectedItem("Dashboard"));
    dispatch(setStatusList([]));

    let savedSettingData = JSON.parse(localStorage.getItem("taskDataKeep"));
    if (savedSettingData) {
      savedSettingData.assignees = [];
      localStorage.setItem("taskDataKeep", JSON.stringify(savedSettingData));
    }
    if (project === "My Projects") {
      navigate(`/defect-tracker/projects`);
    } else {
      addRecentProject(project);
      navigate(
        `/defect-tracker/projects/${project._id}/${project.boardType === "SCRUM" ? "backlog" : "task-board"}`,
      );
    }
  };

  // Filter members based on search input and filtered users - Updated to use getFilteredUsers
  const filteredMembers = getFilteredUsers()?.filter(
    (item: any) =>
      !memberSearch ||
      `${item?.user?.firstName} ${item?.user?.lastName}`
        .toLowerCase()
        .includes(memberSearch.toLowerCase()),
  );

  const showUnassigned =
    !memberSearch ||
    "unassigned".toLowerCase().includes(memberSearch.toLowerCase());

  const showSelectAll =
    !memberSearch ||
    "select all".toLowerCase().includes(memberSearch.toLowerCase());

  useEffect(() => {
    if (pathname === "/") {
      setSelectedProject(t("My Projects"));
    } else {
      setSelectedProject(projectDetails?.name || "");
    }
  }, [pathname, projectDetails]);

  // Updated: Initialize SCRUM board to show All Sprints view and set appropriate view type
  useEffect(() => {
    if (isMyTasks || isMyTaskView) return;

    // MODIFIED: Only set default views if selectedItem is not already set
    if (!selectedItem || selectedItem === "Dashboard") {
      if (boardType === "SCRUM") {
        dispatch(setSelectedItem("Dashboard")); // Changed from "Backlog"
        dispatch(setBacklogViewType("backlog"));
        dispatch(setViewType("backlog"));
      } else if (boardType === "KANBAN") {
        dispatch(setSelectedItem("Dashboard")); // Changed from "Task Board"
        dispatch(setViewType("kanban"));
        setCurrentView("kanban");
      }
    }
  }, [boardType, isMyTasks, selectedItem]);
  const handleSprintStart = async (data: any) => {
    try {
      const payload = {
        project: id,
        name: data.name,
        status: "ACTIVE",
        startDate: data.startDate,
        endDate: data.endDate,
        sprintId: data.sprintId,
        description: data.goal,
        duration: data.duration,
      };
      await updateSprint(payload).unwrap();
      toast.success(t("Sprint started successfully"));
      setSprintStartModalOpen(false);
      refetchSprints();
      dispatch(setSelectedItem("All Sprints"));
      dispatch(setBacklogViewType("allSprint"));
      dispatch(setViewType("allSprint"));
    } catch (error) {
      console.error("Error starting sprint:", error);
      toast.error(t("Failed to start sprint"));
    }
  };
  // Get non-recent projects
  const getNonRecentProjects = () => {
    const recentIds = getRecentProjects().map((item) => item._id);
    return allProjects.filter((project) => !recentIds.includes(project._id));
  };

  // Determine if board view should be shown
  const boardView =
    selectedItem === "Task Board" ||
    isMyTaskView ||
    selectedItem === "Recurring Tasks" ||
    selectedItem === "Backlog" ||
    selectedItem === "Feedback" ||
    selectedItem.toLowerCase().includes("sprint") ||
    selectedItem === "Retrospectives";

  // Check if all users are selected (including unassigned) - Updated to use filtered users
  const filteredUsers = getFilteredUsers();
  const allUserIds =
    filteredUsers?.map((user) => user?.user?._id || user._id) || [];
  const allIdsWithUnassigned = [...allUserIds, "unassigned"];
  const isAllSelected =
    allIdsWithUnassigned.length > 0 &&
    allIdsWithUnassigned.every((id) => selectedAvatars.includes(id));

  // Check if we should show the sprint breadcrumb navigation
  const shouldShowSprintNavigation =
    (selectedItem === "All Sprints" ||
      selectedItem.toLowerCase().includes("sprint")) &&
    viewType !== "dashboard";

  const renderToggleButtons = () => {
    if (isMyTaskView) {
      return (
        <ToggleButtonGroup
          value={currentView}
          exclusive
          onChange={handleViewChange}
          aria-label="view mode"
          size="small"
          sx={{
            backgroundColor: theme.palette.action.hover,
            borderRadius: "30px",
            border: "none",
            "& .MuiToggleButtonGroup-grouped": {
              border: "none",
              borderRadius: "30px !important",
              mx: 0.3,
              py: 0.5,
              px: 1,
              color: theme.palette.text.secondary,
              "&.Mui-selected": {
                backgroundColor: theme.palette.background.paper,
                color: (theme) => theme.palette.primary.main,
                boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                fontWeight: 500,
              },
            },
          }}
        >
          <ToggleButton value="kanban" aria-label="kanban view">
            <ViewKanbanIcon sx={{ mr: 0.5, fontSize: "12px" }} />
            {t("Board")}
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon sx={{ mr: 0.5, fontSize: "12px" }} />
            {t("List")}
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableChartIcon sx={{ mr: 0.5, fontSize: "12px" }} />
            {t("Table")}
          </ToggleButton>
        </ToggleButtonGroup>
      );
    }
    // For SCRUM board type
    if (boardType === "SCRUM") {
      // For All Sprints view OR any individual sprint view (but NOT Backlog)
      if (
        selectedItem === "All Sprints" ||
        (selectedItem?.toLowerCase().includes("sprint") &&
          selectedItem !== "Backlog" &&
          selectedItem !== "Dashboard" &&
          !selectedItem.toLowerCase().includes("backlog"))
      ) {
        return (
          <ToggleButtonGroup
            value={backlogViewType}
            exclusive
            onChange={handleBacklogViewChange}
            aria-label="view mode"
            size="small"
            sx={{
              backgroundColor: theme.palette.action.hover,
              borderRadius: "30px",
              border: "none",

              "& .MuiToggleButtonGroup-grouped": {
                border: "none",
                borderRadius: "30px !important",
                mx: 0.3,
                py: 0.5,
                px: 1,
                color: theme.palette.text.secondary,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.background.paper,
                  color: (theme) => theme.palette.primary.main,
                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                  fontWeight: 500,
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  pointerEvents: "none",
                },
              },
            }}
          >
            {selectedItem === "All Sprints" && (
              <ToggleButton value="allSprint" aria-label="all sprints view">
                <TableChartIcon sx={{ mr: 0.5, fontSize: "12px" }} />
                {t("All Sprints")}
              </ToggleButton>
            )}

            <ToggleButton value="kanban" aria-label="board view">
              <ViewKanbanIcon sx={{ mr: 0.5, fontSize: "12px" }} />
              {t("Board")}
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon sx={{ mr: 0.5, fontSize: "12px" }} />
              {t("List")}
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view detail">
              <TableChartIcon sx={{ mr: 0.5, fontSize: "12px" }} />
              {t("Table")}
            </ToggleButton>
            {selectedItem !== "All Sprints" && (
              <ToggleButton value="workload" aria-label="workload view">
                <MenuOutlinedIcon sx={{ mr: 0.5, fontSize: "12px" }} />
                {t("Workload")}
              </ToggleButton>
            )}
            <ToggleButton value="timeline" aria-label="timeline view">
              <Timeline sx={{ mr: 0.5, fontSize: "12px" }} />
              {t("Timeline")}
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarToday sx={{ mr: 0.5, fontSize: "12px" }} />
              {t("Calendar")}
            </ToggleButton>
          </ToggleButtonGroup>
        );
      }

      if (selectedItem === "Backlog") {
        return (
          <ToggleButtonGroup
            value={backlogViewType}
            exclusive
            onChange={handleBacklogViewChange}
            aria-label="view mode"
            size="small"
            sx={{
              backgroundColor: theme.palette.action.hover,
              borderRadius: "30px",
              border: "none",

              "& .MuiToggleButtonGroup-grouped": {
                border: "none",
                borderRadius: "30px !important",
                mx: 0.3,
                py: 0.5,
                px: 1,
                color: theme.palette.text.secondary,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.background.paper,
                  color: (theme) => theme.palette.primary.main,
                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                  fontWeight: 500,
                },
              },
            }}
          >
            {" "}
            {/* <ToggleButton
              value="dashboard"
              aria-label="dashboard view"
              // disabled={true}
            >
              <DashboardCustomizeOutlinedIcon
                sx={{ mr: 0.5, fontSize: "12px" }}
              />
              {t("Dashboard")}
            </ToggleButton> */}
            <ToggleButton value="board" aria-label="board view">
              <MenuOutlinedIcon sx={{ mr: 0.5, fontSize: "12px" }} />
              {t("Board")}
            </ToggleButton>
            <ToggleButton value="backlog" aria-label="table view">
              <TableChartIcon sx={{ mr: 0.5, fontSize: "12px" }} />
              {t("Table")}
            </ToggleButton>
          </ToggleButtonGroup>
        );
      }
    }

    // For KANBAN board type (non-SCRUM) - only show for Task Board
    if (boardType === "KANBAN" && selectedItem === "Task Board") {
      return (
        <ToggleButtonGroup
          value={currentView}
          exclusive
          onChange={handleViewChange}
          aria-label="view mode"
          size="small"
          sx={{
            backgroundColor: theme.palette.action.hover,
            borderRadius: "30px",
            border: "none",
            "& .MuiToggleButtonGroup-grouped": {
              border: "none",
              borderRadius: "30px !important",
              mx: 0.3,
              py: 0.5,
              px: 1,
              color: theme.palette.text.secondary,
              "&.Mui-selected": {
                backgroundColor: theme.palette.background.paper,
                color: (theme) => theme.palette.primary.main,
                boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                fontWeight: 500,
              },
            },
          }}
        >
          {/* <ToggleButton
            value="dashboard"
            aria-label="dashboard view"
            // disabled={true}
          >
            <DashboardCustomizeOutlinedIcon
              sx={{ mr: 0.5, fontSize: "12px" }}
            />
            {t("Dashboard")}
          </ToggleButton> */}
          <ToggleButton value="kanban" aria-label="kanban view">
            <ViewKanbanIcon sx={{ mr: 0.5, fontSize: "12px" }} />
            {t("Board")}
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon sx={{ mr: 0.5, fontSize: "12px" }} />
            {t("List")}
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableChartIcon sx={{ mr: 0.5, fontSize: "  12px" }} />
            {t("Table")}
          </ToggleButton>
        </ToggleButtonGroup>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        overflowX: "auto",
        overflowY: "hidden",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {!pathname.includes("defect-tracker/settings") &&
        !pathname.includes("/feedback-config") && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent:
                view !== "projectPage" ? "space-between" : "flex-end",
              borderBottom: "1px solid",
              borderColor: "divider",
              minHeight: "52px",
            }}
            px={3}
            // py={1.65}
          >
            {view !== "projectPage" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: "primary.main",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                    p={0}
                    m={0}
                    onClick={handleClick}
                  >
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      &gt;
                    </Box>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        backgroundColor:
                          projectDetails?.icon || theme.palette.primary.main,
                        fontSize: "12px",
                      }}
                    >
                      {isMyTasks
                        ? "MT"
                        : projectDetails?.projectKey?.slice(0, 2) ||
                          selectedProject?.slice(0, 1)}
                    </Avatar>
                    {/* <Box component="span" sx={{ color: "text.secondary" }}>
                    {companyDetails?.companyName}
                  </Box> */}
                    {isMyTasks || isMyTaskView ? (
                      <Box
                        component="span"
                        sx={{ color: "text.primary", fontWeight: 600 }}
                      >
                        {t("My Tasks")}
                      </Box>
                    ) : (
                      <Box
                        component="span"
                        sx={{ color: "text.primary", fontWeight: 600 }}
                      >
                        {selectedProject}
                      </Box>
                    )}
                  </Typography>
                </Box>
              </>
            )}
            {!isSniffer && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                {renderToggleButtons()}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "center",
                alignContent: "center",
              }}
            >
              {/* {!isCurrentUserClient && <TaskGlobalSearch />} */}
              {isFeedback && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TuneOutlinedIcon />}
                  onClick={() => {
                    dispatch(setSelectedItem("Feedback Config"));
                    navigate(`/defect-tracker/projects/${id}/feedback-config`);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "6px",
                    padding: "4px 12px",
                    fontSize: "12px",
                  }}
                >
                  {t("Configure Widget")}
                </Button>
              )}
              {isFeedback && projectDetails?.domain && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() => {
                    let url = projectDetails.domain;
                    if (
                      !url.startsWith("http://") &&
                      !url.startsWith("https://")
                    ) {
                      url = "https://" + url;
                    }
                    window.open(url, "_blank");
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "6px",
                    padding: "4px 12px",
                    fontSize: "12px",
                  }}
                >
                  {t("Visit Website")}
                </Button>
              )}
              {!DEFEKT_TRACKER.HIDE_CREATE(myPermissions) &&
                activeSprintIds?.length > 0 &&
                selectedItem?.toLowerCase().includes("sprint") &&
                selectedSprint?.status !== "ARCHIVE" && (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => {
                        if (selectedSprint?.status === "DRAFT") {
                          setSprintStartModalOpen(true);
                        } else {
                          setIsSprintCompleteModalOpen(true);
                        }
                      }}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "12px",
                        minWidth: "120px",
                      }}
                      disabled={!DEFEKT_TRACKER.CREATE(myPermissions)}
                    >
                      {selectedSprint?.status !== "ARCHIVE" &&
                        ((selectedSprint &&
                          selectedSprint?.status === "ACTIVE") ||
                        selectedItem === "All Sprints"
                          ? t("Complete Sprint")
                          : t("Start Sprint"))}
                    </Button>
                  </>
                )}
              {!DEFEKT_TRACKER.HIDE_CREATE(myPermissions) && boardView && (
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    dispatch(setCreateTask(true));
                  }}
                  sx={{
                    borderRadius: "6px",
                    padding: "4px 12px",
                    fontSize: "12px",
                    minWidth: "120px",
                  }}
                  disabled={!DEFEKT_TRACKER.CREATE(myPermissions)}
                >
                  {t("New Task")}
                </Button>
              )}
            </Box>
          </Box>
        )}
      {!detailView ? (
        <TaskCreateModal
          open={boardState?.createTask}
          onClose={handleModalClose}
          pageUrl={pathname}
          users={companyMemberDataQuery?.data?.decryptedData?.items || []}
          onSubmit={handleTaskSubmit}
          isSubmitting={isCreatingBug || isLoadingProjectMembers}
          imageUpload={false}
          refreshColumnData={refreshColumnData}
          setDetailView={setDetailView}
        />
      ) : (
        <TaskCreateDrawer
          open={boardState?.createTask}
          onClose={handleModalClose}
          pageUrl={pathname}
          users={companyMemberDataQuery?.data?.decryptedData?.items || []}
          onSubmit={handleTaskSubmit}
          isSubmitting={isCreatingBug || isLoadingProjectMembers}
          imageUpload={false}
          refreshColumnData={refreshColumnData}
        />
      )}

      {boardView && selectedItem !== "Retrospectives" && (
        <>
          {!isFeedback && (
            <Grid
              container
              sx={{
                padding: "8px 24px 8px 24px",
                borderBottom: "1px solid",
                borderColor: theme.palette.divider,
              }}
              alignItems="center"
            >
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "nowrap",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "nowrap",
                      flex: 1,
                    }}
                  >
                    {shouldShowSprintNavigation && (
                      <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                        sx={{
                          "& .MuiBreadcrumbs-separator": {
                            mx: 0.5,
                          },
                          display: "flex",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: theme.palette.text.secondary,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            "&:hover": {
                              color: theme.palette.primary.main,
                            },
                          }}
                          onClick={() => {}}
                        >
                          Agile
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: theme.palette.text.secondary,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            "&:hover": {
                              color: theme.palette.primary.main,
                            },
                          }}
                          onClick={() => {
                            // Navigate to sprints list
                          }}
                        >
                          Sprints
                        </Typography>
                        {selectedItem !== "All Sprints" && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: theme.palette.text.primary,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {selectedItem.startsWith("Sprint:")
                              ? selectedSprint?.name ||
                                selectedItem.replace("Sprint:", "")
                              : selectedItem}
                          </Typography>
                        )}
                      </Breadcrumbs>
                    )}
                    {viewType === "kanban" && (
                      <GroupBySelector
                        groupBy={groupBy}
                        sortOrder={sortOrder}
                        onGroupByChange={handleGroupByChange}
                        onSortOrderChange={handleSortOrderChange}
                        viewType={viewType}
                        showSubGrouping={showSubGrouping}
                        subGroupBy={subGroupBy}
                        onSubGroupByChange={handleSubGroupByChange}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    {selectedSprint &&
                      selectedSprint.startDate &&
                      selectedSprint.endDate && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 0.5,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.08)"
                                : "rgba(0, 0, 0, 0.04)",
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <CalendarToday
                            sx={{
                              fontSize: "16px",
                              color: theme.palette.text.secondary,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {new Date(
                              selectedSprint.startDate,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                            {" - "}
                            {new Date(
                              selectedSprint.endDate,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </Typography>
                        </Box>
                      )}
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder={t("Search tasks...")}
                      value={searchQuery}
                      onChange={handleSearchChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: 220,
                        "& .MuiOutlinedInput-root": {
                          height: "28px",
                          borderRadius: "20px",
                          "&:hover fieldset": {
                            borderColor: (theme) => theme.palette.primary.main,
                          },
                        },
                      }}
                    />

                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        justifyContent: "center",
                        mr: getFilteredUsers()?.length > 0 ? 1 : 0,
                      }}
                    >
                      {/* Show loading spinner when fetching members */}
                      {isLoadingProjectMembers || isFetchingProjectMembers ? (
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                      ) : (
                        <>
                          {/* Updated to use filtered users instead of all company members */}
                          {getFilteredUsers()
                            ?.slice(0, 3)
                            ?.map((item: any, index: number) => (
                              <Tooltip
                                id="snr-ai-userList-tooltip"
                                key={item?.user?._id || item._id}
                                title={
                                  item?.user?.firstName +
                                  " " +
                                  item?.user?.lastName
                                }
                                arrow
                              >
                                <Avatar
                                  key={item?.user?._id || item._id}
                                  sx={{
                                    backgroundColor: getBackgroundColor(
                                      item?.user?._id || item._id,
                                      id || projectDetails?._id,
                                    ),
                                    marginRight: "-8px",
                                    cursor: "pointer",
                                    position: "relative",
                                    zIndex: 10 - index,
                                    width: "28px",
                                    height: "28px",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    border: (theme) =>
                                      selectedAvatars?.includes(
                                        item?.user?._id || item._id,
                                      )
                                        ? `2px solid ${theme.palette.primary.main} !important`
                                        : `2px solid ${theme.palette.background.paper}`,
                                  }}
                                  onClick={() =>
                                    handleAvatarClick(
                                      item?.user?._id || item._id,
                                    )
                                  }
                                >
                                  {getInitials(
                                    item?.user?.firstName +
                                      " " +
                                      item?.user?.lastName,
                                  )}
                                </Avatar>
                              </Tooltip>
                            ))}
                        </>
                      )}

                      {/* Updated to use filtered users count */}
                      {!isLoadingProjectMembers &&
                        !isFetchingProjectMembers &&
                        getFilteredUsers()?.length >= 1 && (
                          <Avatar
                            onClick={handleMoreClick}
                            sx={{
                              backgroundColor: theme.palette.action.selected,
                              cursor: "pointer",
                              width: "32px",
                              height: "32px",
                              fontSize: "12px",
                              border: `2px solid ${theme.palette.background.paper}`,
                              fontWeight: 500,
                            }}
                          >
                            {getFilteredUsers()?.length > 3 ? (
                              `+${getFilteredUsers()?.length - 3}`
                            ) : (
                              <MoreHorizIcon sx={{ fontSize: 16 }} />
                            )}
                          </Avatar>
                        )}
                    </Box>

                    {boardView && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleTagsClick}
                        startIcon={<LocalOfferOutlinedIcon />}
                        endIcon={<ArrowDropDownIcon />}
                        sx={{
                          borderRadius: "6px",
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                          "&:hover": {
                            borderColor: theme.palette.text.disabled,
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        {t("Tags")}
                        {selectedTags?.length > 0 && (
                          <Typography component="span" ml={0.5}>
                            ({selectedTags?.length})
                          </Typography>
                        )}
                      </Button>
                    )}

                    {/* Refresh Button */}
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          <RefreshIcon sx={{ marginRight: "-.5vw" }} />
                        }
                        onClick={handleRefreshClick}
                        sx={{
                          textTransform: "none",
                          borderRadius: "6px",
                          padding: "4px 12px",
                          minWidth: "36px",
                          width: "36px",
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                          "&:hover": {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          },
                        }}
                        aria-label={t("refresh")}
                      />

                      {/* Small indicator for refresh interval */}
                      {refreshMode !== "manual" && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: theme.palette.primary.main,
                            color: "white",
                            borderRadius: "50%",
                            minWidth: 18,
                            height: 18,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            fontWeight: 600,
                            border: `2px solid ${theme.palette.background.paper}`,
                            zIndex: 1,
                          }}
                        >
                          {refreshMode === "30s" ? "30" : "60"}
                        </Box>
                      )}
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        <FilterListIcon sx={{ marginRight: "-.5vw" }} />
                      }
                      onClick={handleFilterClick}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        padding: "4px 12px",
                        minWidth: "36px",
                        width: "36px",
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                        "&:hover": {
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                        },
                      }}
                      aria-label={t("filter")}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
          {selectedItem !== "Retrospectives" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 3,
                // py: selectedItem === "Task Board" ? 0.5 : 0,
                // borderBottom: "1px solid",
                borderColor: theme.palette.divider,
                flexWrap: "wrap",
                // minHeight: selectedItem === "Task Board" ? "52px" : "auto",
              }}
            >
              <FilterChips />
            </Box>
          )}

          {/* Refresh Menu */}
          <Menu
            anchorEl={anchorElRefresh}
            open={openRefreshMenu}
            onClose={handleCloseRefreshMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                minWidth: 150,
              },
            }}
          >
            <MenuItem onClick={() => handleRefreshModeChange("manual")}>
              <ListItemText primary={t("Manual Refresh")} />
              {refreshMode === "manual" && (
                <ListItemIcon sx={{ justifyContent: "flex-end" }}>
                  <CheckIcon sx={{ color: "white", fontSize: 16 }} />
                </ListItemIcon>
              )}
            </MenuItem>
            <MenuItem onClick={() => handleRefreshModeChange("30s")}>
              <ListItemText primary={t("Every 30s")} />
              {refreshMode === "30s" && (
                <ListItemIcon sx={{ justifyContent: "flex-end" }}>
                  <CheckIcon sx={{ color: "white", fontSize: 16 }} />
                </ListItemIcon>
              )}
            </MenuItem>
            <MenuItem onClick={() => handleRefreshModeChange("60s")}>
              <ListItemText primary={t("Every 60s")} />
              {refreshMode === "60s" && (
                <ListItemIcon sx={{ justifyContent: "flex-end" }}>
                  <CheckIcon sx={{ color: "white", fontSize: 16 }} />
                </ListItemIcon>
              )}
            </MenuItem>
          </Menu>

          {/* Updated More Menu to use filtered users */}
          <Menu
            anchorEl={anchorElMore}
            open={openMoreMenu}
            onClose={handleCloseMoreMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            MenuListProps={{
              // Prevent click events from closing the menu
              onClick: (e) => e.stopPropagation(),
            }}
          >
            <Box sx={{ p: 1, width: "250px" }}>
              <TextField
                inputRef={searchInputRef}
                size="small"
                fullWidth
                variant="outlined"
                placeholder={t("Search members...")}
                value={memberSearch}
                onChange={handleMemberSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>
            <Divider />

            <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
              {/* Add Select All option at the top */}
              {showSelectAll && (
                <MenuItem
                  onClick={handleSelectAll}
                  sx={{
                    padding: "8px 16px",
                    height: "40px",
                    backgroundColor: isAllSelected
                      ? theme.palette.action.selected
                      : "transparent",
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={isAllSelected}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      transform: "scale(0.8)",
                    }}
                  />
                  <Avatar
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      marginRight: 1,
                      width: 24,
                      height: 24,
                      fontSize: "10px",
                      border: `1px solid ${theme.palette.background.paper}`,
                      fontWeight: 500,
                    }}
                  >
                    <SelectAllIcon style={{ fontSize: "14px" }} />
                  </Avatar>
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                    }}
                  >
                    {t("Select All")}
                  </Typography>
                </MenuItem>
              )}

              {/* Add divider if select all is shown */}
              {showSelectAll && <Divider />}

              {/* Add Unassigned option */}
              {showUnassigned && (
                <MenuItem
                  onClick={() => handleAvatarClick("unassigned")}
                  sx={{
                    padding: "8px 16px",
                    height: "40px",
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedAvatars?.includes("unassigned")}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      transform: "scale(0.8)",
                    }}
                  />
                  <Avatar
                    sx={{
                      backgroundColor: theme.palette.grey[500],
                      marginRight: 1,
                      width: 24,
                      height: 24,
                      fontSize: "10px",
                      border: `1px solid ${theme.palette.background.paper}`,
                      fontWeight: 500,
                    }}
                  >
                    <PersonOutlineIcon style={{ fontSize: "14px" }} />
                  </Avatar>
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("Unassigned")}
                  </Typography>
                </MenuItem>
              )}

              {/* Add divider if both unassigned and members are shown */}
              {showUnassigned && filteredMembers?.length > 0 && <Divider />}

              {/* Updated member list to use filtered users */}
              {filteredMembers?.length > 0
                ? filteredMembers.map((item: any, index: number) => (
                    <MenuItem
                      key={index}
                      onClick={() =>
                        handleAvatarClick(item?.user?._id || item._id)
                      }
                      sx={{
                        padding: "8px 16px",
                        height: "40px",
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={selectedAvatars?.includes(
                          item?.user?._id || item._id,
                        )}
                        tabIndex={-1}
                        disableRipple
                        sx={{
                          transform: "scale(0.8)",
                        }}
                      />
                      <Avatar
                        sx={{
                          backgroundColor: getBackgroundColor(
                            item?.user?._id || item._id,
                            id || projectDetails?._id,
                          ),
                          marginRight: 1,
                          width: 24,
                          height: 24,
                          fontSize: "10px",
                          border: `1px solid ${theme.palette.background.paper}`,
                          fontWeight: 500,
                        }}
                      >
                        {getInitials(
                          item?.user?.firstName + " " + item?.user?.lastName,
                        )}
                      </Avatar>
                      <Tooltip
                        id="snr-ai-userList-tooltip"
                        key={item?.user?._id || item._id}
                        title={
                          item?.user?.firstName + " " + item?.user?.lastName
                        }
                        arrow
                      >
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item?.user?.firstName + " " + item?.user?.lastName}
                        </Typography>
                      </Tooltip>
                    </MenuItem>
                  ))
                : // Update no members found condition
                  !showUnassigned &&
                  !showSelectAll && (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("No members found")}
                      </Typography>
                    </Box>
                  )}
            </Box>
          </Menu>

          <Menu
            anchorEl={anchorElTags}
            open={openTagsMenu}
            onClose={handleCloseTagsMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {tagList.length > 0 ? (
              tagList?.map((tag) => (
                <MenuItem
                  key={tag._id}
                  onClick={() => handleTagSelect(tag.tagName)}
                  sx={{
                    padding: "8px 16px",
                    height: "32px",
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedTags?.includes(tag.tagName)}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      transform: "scale(0.8)",
                    }}
                  />
                  {tag.tagName}
                </MenuItem>
              ))
            ) : (
              <MenuItem>
                <Typography variant="body2" color="text.secondary">
                  {t("No tags found")}
                </Typography>
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "10px",
              boxShadow:
                "0px 4px 20px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px rgba(0, 0, 0, 0.05)",
              mt: 0.5,
              width: 320,
              maxHeight: "none",
              overflow: "visible",
            },
          },
        }}
        MenuListProps={{
          sx: {
            p: 0,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pt: 1.5,
            pb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "text.disabled",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            {t("Switch Project")}
          </Typography>
          <Typography
            onClick={() => {
              handleClose();
              navigate("/defect-tracker/projects");
            }}
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: theme.palette.primary.main,
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {t("Show all projects")}
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder={t("Search projects...")}
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: "18px", color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "36px",
                borderRadius: "8px",
                fontSize: "13px",
                backgroundColor: theme.palette.action.hover,
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.divider,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: "1.5px",
                },
              },
            }}
          />
        </Box>

        <Divider />

        {/* Project list */}
        {!isLoading && !isFetching ? (
          <Box
            ref={projectListRef}
            onScroll={handleProjectListScroll}
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              px: 0.75,
              py: 0.75,
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.action.disabled,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: theme.palette.text.disabled,
              },
            }}
          >
            {!projectSearch ? (
              <>
                {getRecentProjects().length > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "text.disabled",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                        px: 1,
                        pt: 0.5,
                        pb: 0.5,
                      }}
                    >
                      {t("Recent")}
                    </Typography>
                    {getRecentProjects().map((project) => (
                      <MenuItem
                        key={`recent-${project._id}`}
                        onClick={() => handleProjectSelect(project)}
                        sx={{
                          borderRadius: "6px",
                          px: 1.5,
                          py: 1,
                          minHeight: "unset",
                          gap: 1.5,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: 600,
                            backgroundColor:
                              project?.icon || theme.palette.primary.main,
                          }}
                        >
                          {project?.projectKey?.slice(0, 2) ||
                            project?.name?.slice(0, 2)?.toUpperCase()}
                        </Avatar>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "text.primary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: 1.3,
                            }}
                          >
                            {project.name}
                          </Typography>
                          {project.projectKey && (
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "text.disabled",
                                lineHeight: 1.3,
                              }}
                            >
                              {project.projectKey}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                    {getNonRecentProjects().length > 0 && (
                      <Divider sx={{ my: 0.75 }} />
                    )}
                  </>
                )}

                {getNonRecentProjects().length > 0 && (
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "text.disabled",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      px: 1,
                      pt: 0.5,
                      pb: 0.5,
                    }}
                  >
                    {t("All Projects")}
                  </Typography>
                )}
                {getNonRecentProjects().map((project) => (
                  <MenuItem
                    key={project._id}
                    onClick={() => handleProjectSelect(project)}
                    sx={{
                      borderRadius: "6px",
                      px: 1.5,
                      py: 1,
                      minHeight: "unset",
                      gap: 1.5,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 600,
                        backgroundColor:
                          project?.icon || theme.palette.primary.main,
                      }}
                    >
                      {project?.projectKey?.slice(0, 2) ||
                        project?.name?.slice(0, 2)?.toUpperCase()}
                    </Avatar>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.3,
                        }}
                      >
                        {project.name}
                      </Typography>
                      {project.projectKey && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "text.disabled",
                            lineHeight: 1.3,
                          }}
                        >
                          {project.projectKey}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </>
            ) : (
              <>
                {allProjects.length > 0 ? (
                  allProjects.map((project) => (
                    <MenuItem
                      key={project._id}
                      onClick={() => handleProjectSelect(project)}
                      sx={{
                        borderRadius: "6px",
                        px: 1.5,
                        py: 1,
                        minHeight: "unset",
                        gap: 1.5,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          backgroundColor:
                            project?.icon || theme.palette.primary.main,
                        }}
                      >
                        {project?.projectKey?.slice(0, 2) ||
                          project?.name?.slice(0, 2)?.toUpperCase()}
                      </Avatar>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "text.primary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: 1.3,
                          }}
                        >
                          {project.name}
                        </Typography>
                        {project.projectKey && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "text.disabled",
                              lineHeight: 1.3,
                            }}
                          >
                            {project.projectKey}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography
                      sx={{ fontSize: "13px", color: "text.secondary" }}
                    >
                      {t("No projects found")}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Loading indicator for loading more projects */}
            {isLoadingMoreProjects && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={1.5}
                gap={1}
              >
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                  {t("Loading more...")}
                </Typography>
              </Box>
            )}

            {/* Show message when no more projects to load */}
            {!hasMoreProjects && allProjects.length > 5 && (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "text.disabled",
                  textAlign: "center",
                  py: 0.75,
                }}
              >
                {t("All projects loaded")}
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={5}
          >
            <CircularProgress size={22} />
          </Box>
        )}

        <Divider />

        {/* Footer navigation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
          }}
        >
          <MenuItem
            onClick={() => handleProjectSelect("My Projects")}
            sx={{
              borderRadius: "6px",
              px: 1,
              py: 0.5,
              minHeight: "unset",
              gap: 1,
              flex: 1,
              "& .MuiSvgIcon-root": {
                fontSize: "16px",
                color: theme.palette.text.secondary,
              },
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <FolderOutlinedIcon />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "text.secondary",
              }}
            >
              {t("My Projects")}
            </Typography>
          </MenuItem>
          <MenuItem
            disabled
            sx={{
              borderRadius: "6px",
              px: 1,
              py: 0.5,
              minHeight: "unset",
              gap: 1,
              flex: 1,
              "& .MuiSvgIcon-root": {
                fontSize: "16px",
              },
              "&.Mui-disabled": {
                opacity: 0.4,
              },
            }}
          >
            <DashboardCustomizeOutlinedIcon />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "text.secondary",
              }}
            >
              {t("Dashboard")}
            </Typography>
          </MenuItem>
        </Box>
      </Menu>

      <TaskFilterMenu
        anchorEl={anchorElFilter}
        open={openFilterMenu}
        onClose={handleCloseFilterMenu}
        // onSaveFilters={onSaveFilters} // Optional: only pass if you have save functionality
      />

      {isSprintCompleteModalOpen && (
        <SprintCompleteModal
          open={isSprintCompleteModalOpen}
          onClose={handleCloseSprintCompleteModal}
          onComplete={handleSprintComplete}
          draftSprints={[]}
          sprint={selectedSprint?._id}
          isLoading={isUpdatingSprintStatus}
        />
      )}
      {sprintStartModalOpen && (
        <SprintStartModal
          open={sprintStartModalOpen}
          onClose={() => setSprintStartModalOpen(false)}
          onStart={handleSprintStart}
          sprint={selectedSprint}
          taskCount={0}
          isLoading={isUpdatingSprint}
          isEditMode={false}
        />
      )}

      <Box sx={{ px: 3, py: 2 }}>{children}</Box>
    </Box>
  );
}
